import React from 'react';
import { Link } from 'react-router-dom';
import { SEO, SEOConfig } from './SEO';
import { MainFooter } from './Footer';
import { Header } from './Header';

const NachhaltigkeitPage = () => {
  return (
    <>
      <SEO {...SEOConfig.nachhaltigkeit} />
      <div className="min-h-screen bg-white">
        <Header />

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          {/* Page Header */}
          <div className="text-center mb-32">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Nachhaltigkeit
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-700 to-blue-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700">
              100% Ökostrom | CO2-neutral | CSRD-konform
            </p>
          </div>

          {/* Content */}
          <div>
            <div className="prose prose-lg max-w-none">

                {/* Intro */}
                <div className="border-l-4 border-gray-300 p-6 mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">
                    Digitalisierung mit Verantwortung
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    Als Cloud-Software-Anbieter tragen wir Verantwortung für unseren ökologischen Fußabdruck.
                    Deshalb setzen wir auf <strong>100% erneuerbare Energie</strong> und betreiben unsere Server
                    ausschließlich in <strong>hocheffizienten deutschen Rechenzentren</strong>.
                  </p>
                  <p className="text-gray-700 mt-3">
                    <strong>EU-Vorgabe CSRD:</strong> Ab 2025 müssen Unternehmen die Nachhaltigkeit ihrer IT-Dienstleister
                    offenlegen. Mit IDA sind Sie bereits compliant.
                  </p>
                </div>

                {/* Key Metrics */}
                <section className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Unsere Nachhaltigkeits-Bilanz
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Metric Card 1 */}
                    <div className="border-l-4 border-gray-300 p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">100% Ökostrom</h3>
                      <p className="text-gray-700">
                        Unsere Server laufen seit 2008 ausschließlich mit <strong>Wasserkraft</strong> aus deutschen Kraftwerken.
                        Keine fossilen Brennstoffe, keine Atomenergie.
                      </p>
                    </div>

                    {/* Metric Card 2 */}
                    <div className="border-l-4 border-gray-300 p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">77.000 t CO₂</h3>
                      <p className="text-gray-700">
                        <strong>Einsparung pro Jahr</strong> im Vergleich zum deutschen Strommix durch unseren Hosting-Partner Hetzner.
                      </p>
                    </div>

                    {/* Metric Card 3 */}
                    <div className="border-l-4 border-gray-300 p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">98% natürliche Kühlung</h3>
                      <p className="text-gray-700">
                        Kühlung durch <strong>Außenluft</strong> statt wasserintensiver Klimaanlagen.
                        Kein Wasserverbrauch für Rechenzentrum-Kühlung.
                      </p>
                    </div>

                    {/* Metric Card 4 */}
                    <div className="border-l-4 border-gray-300 p-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-3">8 Jahre Lebensdauer</h3>
                      <p className="text-gray-700">
                        Server-Lebensdauer von durchschnittlich <strong>8 Jahren</strong> statt 3-4 Jahren bei US-Hyperscalern.
                        Weniger E-Müll, mehr Nachhaltigkeit.
                      </p>
                    </div>
                  </div>
                </section>

                {/* PUE */}
                <section className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Energie-Effizienz (PUE)
                  </h2>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="space-y-4 text-gray-700">
                      <p>
                        <strong>Power Usage Effectiveness (PUE):</strong> Misst die Effizienz eines Rechenzentrums.
                        Ein Wert von 1.0 wäre perfekt (100% Energie geht in Server).
                      </p>
                      <div className="bg-white border-l-4 border-green-500 p-4 rounded">
                        <p className="text-2xl font-bold text-green-700">PUE: 1.10 - 1.16</p>
                        <p className="text-sm text-gray-600">Durchschnitt: 1.13 (Hetzner Rechenzentren)</p>
                      </div>
                      <p className="text-sm text-gray-600">
                        <strong>Zum Vergleich:</strong>
                      </p>
                      <ul className="text-sm list-disc list-inside ml-4 space-y-1">
                        <li>Branchendurchschnitt: 1.6</li>
                        <li>AWS/Azure typisch: 1.4-1.8</li>
                        <li>IDA (Hetzner): <strong className="text-green-700">1.13 = 28% effizienter</strong></li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Weitere Maßnahmen */}
                <section className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Konkrete Nachhaltigkeits-Maßnahmen
                  </h2>

                  <div className="space-y-6">
                    <div className="border-l-4 border-gray-300 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">100% Wasserkraft seit 2008</h3>
                      <p className="text-gray-700">
                        Alle deutschen Hetzner-Rechenzentren nutzen ausschließlich Ökostrom aus Wasserkraft.
                        Zertifikate für 2025-2026 verfügbar.
                      </p>
                    </div>

                    <div className="border-l-4 border-gray-300 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Wasserfreie Kühlung</h3>
                      <p className="text-gray-700">
                        98% des Jahres erfolgt die Kühlung durch natürliche Außenluft.
                        Keine wasserintensive Klimatisierung erforderlich (Water Usage Effectiveness: nahezu 0).
                      </p>
                    </div>

                    <div className="border-l-4 border-gray-300 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Abwärme-Nutzung</h3>
                      <p className="text-gray-700">
                        Server-Abwärme wird zum Heizen von Büros und Produktionsflächen genutzt.
                        Keine Energie-Verschwendung.
                      </p>
                    </div>

                    <div className="border-l-4 border-gray-300 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Verlängerte Server-Lebensdauer</h3>
                      <p className="text-gray-700">
                        Durchschnittlich <strong>8 Jahre Nutzungsdauer</strong> statt 3-4 Jahre bei Hyperscalern.
                        Weniger E-Müll, mehr Nachhaltigkeit durch Hetzner's "Serverbörse" (Wiederverwendung).
                      </p>
                    </div>

                    <div className="border-l-4 border-gray-300 p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Modulare Rechenzentren</h3>
                      <p className="text-gray-700">
                        Neueste Optimierungen in jedem Modul. Energie-Monitoring-System überwacht
                        Verbrauch in Echtzeit und optimiert kontinuierlich.
                      </p>
                    </div>
                  </div>
                </section>

                {/* EU-Compliance */}
                <section className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    EU-Nachhaltigkeits-Vorgaben (CSRD)
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p>
                      Die <strong>Corporate Sustainability Reporting Directive (CSRD)</strong> verpflichtet EU-Unternehmen ab 2025,
                      die Nachhaltigkeit ihrer Lieferanten und Dienstleister offenzulegen.
                    </p>
                    <div className="border-l-4 border-gray-300 p-5">
                      <h3 className="font-semibold text-gray-900 mb-3">Mit IDA sind Sie CSRD-compliant:</h3>
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        <li>Transparente CO2-Bilanz Ihrer IT-Infrastruktur</li>
                        <li>100% erneuerbare Energie (Wasserkraft, zertifiziert)</li>
                        <li>Nachweisbare Energie-Effizienz (PUE 1.13)</li>
                        <li>Deutscher Hosting-Partner mit Nachhaltigkeits-Zertifikaten</li>
                        <li>Keine intransparenten US-Cloud-Rechenzentren</li>
                      </ul>
                    </div>
                    <p className="text-sm text-gray-600">
                      Viele US-Cloud-Anbieter (AWS, Azure, Google Cloud) bieten diese Transparenz nur Premium-Kunden
                      oder gar nicht. Mit IDA haben Sie alle Nachweise für Ihr Nachhaltigkeits-Reporting.
                    </p>
                  </div>
                </section>

                {/* Vergleich */}
                <section className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Vergleich: IDA vs. US-Cloud-Lösungen
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left p-3 border-b-2 border-gray-300">Kriterium</th>
                          <th className="text-left p-3 border-b-2 border-gray-300">IDA (Hetzner)</th>
                          <th className="text-left p-3 border-b-2 border-gray-300">AWS/Azure/GCP</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-200">
                          <td className="p-3 font-medium">Energiequelle</td>
                          <td className="p-3 text-gray-900"><strong>100% Wasserkraft</strong></td>
                          <td className="p-3 text-gray-600">Mix (oft fossil)</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <td className="p-3 font-medium">CO2-Bilanz</td>
                          <td className="p-3 text-gray-900"><strong>-77.000t/Jahr</strong></td>
                          <td className="p-3 text-gray-600">Oft positiv</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="p-3 font-medium">PUE-Effizienz</td>
                          <td className="p-3 text-gray-900"><strong>1.13</strong></td>
                          <td className="p-3 text-gray-600">1.4-1.8</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <td className="p-3 font-medium">Kühlung</td>
                          <td className="p-3 text-gray-900"><strong>98% Außenluft</strong></td>
                          <td className="p-3 text-gray-600">Wasserintensiv</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="p-3 font-medium">Server-Lebensdauer</td>
                          <td className="p-3 text-gray-900"><strong>8 Jahre</strong></td>
                          <td className="p-3 text-gray-600">3-4 Jahre</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <td className="p-3 font-medium">Transparenz</td>
                          <td className="p-3 text-gray-900"><strong>Alle Daten öffentlich</strong></td>
                          <td className="p-3 text-gray-600">Oft intransparent</td>
                        </tr>
                        <tr>
                          <td className="p-3 font-medium">CSRD-Compliance</td>
                          <td className="p-3 text-gray-900"><strong>Ja</strong></td>
                          <td className="p-3 text-gray-600">Teilweise</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Hetzner Partner */}
                <section className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Unser Partner: Hetzner Online GmbH
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p>
                      IDA setzt auf <strong>Hetzner</strong> als Hosting-Partner - ein deutsches Familienunternehmen
                      mit über 25 Jahren Erfahrung im Rechenzentrumsbetrieb.
                    </p>

                    <div className="border-l-4 border-gray-300 p-5">
                      <h3 className="font-semibold text-gray-900 mb-3">Hetzner's Nachhaltigkeits-Commitment:</h3>
                      <ul className="list-disc list-inside space-y-2 text-gray-700">
                        <li>100% Ökostrom (Wasserkraft) seit 2008</li>
                        <li>ISO/IEC 27001:2022 zertifiziert</li>
                        <li>Standorte: Falkenstein, Nürnberg (Deutschland), Helsinki (Finnland)</li>
                        <li>Dedizierter Information Security Officer & Data Protection Manager</li>
                        <li>Modulare Rechenzentren mit neuesten Effizienz-Standards</li>
                        <li>Energieverbrauch-Monitoring in Echtzeit</li>
                        <li>Eigene Hardware-Entwicklung (energieeffizient)</li>
                      </ul>
                    </div>

                    <p className="text-sm">
                      Mehr Informationen: <a href="https://www.hetzner.com/de/unternehmen/nachhaltigkeit/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Hetzner Nachhaltigkeit
                      </a>
                    </p>
                  </div>
                </section>

                {/* Ihre Vorteile */}
                <section className="mb-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Ihre Vorteile mit IDA's Nachhaltigkeit
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border-l-4 border-gray-300 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">CSRD-Reporting leicht gemacht</h4>
                      <p className="text-sm text-gray-700">
                        Alle Nachhaltigkeitsdaten Ihrer IT-Infrastruktur transparent und nachweisbar.
                      </p>
                    </div>

                    <div className="border-l-4 border-gray-300 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Echter CO2-Fußabdruck-Reduktion</h4>
                      <p className="text-sm text-gray-700">
                        Jeder IDA-Kunde trägt zur 77.000t CO2-Einsparung pro Jahr bei.
                      </p>
                    </div>

                    <div className="border-l-4 border-gray-300 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Wettbewerbsvorteil</h4>
                      <p className="text-sm text-gray-700">
                        "100% Ökostrom" als Verkaufsargument gegenüber Ihren Kunden.
                      </p>
                    </div>

                    <div className="border-l-4 border-gray-300 p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Zukunftssicher</h4>
                      <p className="text-sm text-gray-700">
                        EU-Vorgaben bereits heute erfüllt - kein Handlungsbedarf 2025.
                      </p>
                    </div>
                  </div>
                </section>

            </div>
          </div>
        </main>

        <MainFooter />
      </div>
    </>
  );
};

export default NachhaltigkeitPage;
